

import { ExecuteImport, RetrieveConnectionTokenWithErrorHandling } from '../../components/FinancialsImport/Common';
import { store } from '../../index';
import { ExcelUploadAccountingPackageNameId, toAccountingPackageNameId, UniversalCollectorAccountingPackageNameId } from '../../Models/Api/AccountingPackage';
import { AccountingPackage } from '../../Models/Api/AccountingPackages';
import { AccountingPackage as AccountingPackageDefinition } from '../../Models/Api/strongbox.financialportal';
import { AppCookieData } from '../../Models/CookieData';
import {
    PortalCreateSubmissionRequestOptions,
    PortalSubmissionRequestFromSubmissionRequest,
} from '../../Models/PortalCreateSubmissionRequestOptions';
import { OrganizationsService } from '../../Services/OrganizationsService';
import { CreateSubmissionRequest, FetchSubmissionRequest, } from '../../Services/SubmissionRequestsService';
import { CreateSubmission } from '../../Services/SubmissionService';
import { AttributedParagraphSet } from '../../Utils/AttributedParagraph';
import { AppendMetadataToConnectionEndpoint, ConnectAccountingSystem, ConnectUrlParameter, StrongboxConnectionRequest } from '../../Utils/ConnectStrongbox';
import { ConnectWindow, IConnectNavigator } from '../../Utils/ConnectWindow';
import { SubmissionMetaBorrowerBusinessEmail, SubmissionMetaBorrowerBusinessName } from '../../Utils/Constants';
import { LogException, LogMessage, SeverityLevel } from '../../Utils/Logging';
import { ExtractUrlParameters } from '../../Utils/PathUtils';
import { Sleep } from '../../Utils/Sleep';
import { GetStrongboxUrl } from '../AppSettings';
import { BorrowerActionCreators, GetAcceptedBusinessInfo } from '../Borrower';
import { ConnectionOptions, ImportMetadataKeys, LinkMode } from '../ImportFinancials';
import { AppThunkAction, AppThunkActionAsync } from '../index';
import { actionCreators as SubmissionActionCreators } from '../Submission';
import {
    AccountingSystemConnectionState,
    IDataCollectionSubmissionRequest,
    IInitializeCollectionGroupings,
    ISetOptionsAction,
    KnownAction,
    NewDataCollectionParametersActions,
} from './Actions';
import { GetCurrentDataCollectionSubmissionRequest, GetCurrentSubmissionAccountingSystem, GetCurrentSubmissionId, GetCurrentSubmissionRequest, GetRequestedOptionsId } from './Selectors';

function SetNewDataCollectionOptions(options: PortalCreateSubmissionRequestOptions, fiscalYearsToCollect: number): ISetOptionsAction {
    return {
        type: NewDataCollectionParametersActions.SetOptions,
        options,
        fiscalYearsToCollect,
    };
}

function InitializeCollectionGroupings(
    collectionGroupDescriptions: AttributedParagraphSet,
    dynamicTenantSelectionEnabled: boolean
): IInitializeCollectionGroupings {
    return {
        type: NewDataCollectionParametersActions.InitializeCollectionGroupings,
        collectionGroupDescriptions,
        dynamicTenantSelectionEnabled,
    }
}

async function InitializeFromURL(location: Location, defaultSettings: AppCookieData): Promise<void> {
    let entityId: string = '';
    let submissionRequestId: number = -1;
    let optionsId: number = -1;

    ExtractUrlParameters(
        location.href,
        (key: string, value: string) => {
            const lowerKey = key.toLowerCase();

            if (lowerKey === 'eid') {
                entityId = value;
            } else if (lowerKey === 'rid') {
                const numValue = parseInt(value);
                if (!isNaN(numValue)) {
                    submissionRequestId = numValue;
                }
            } else if (lowerKey === 'optionsid') {
                const numValue = parseInt(value);
                if (!isNaN(numValue)) {
                    optionsId = numValue;
                }
            }
        }
    );

    // optionsId is used to 'turn stuff off' downstream. it may or may not appear in the url.
    // entityId and requestId are always available in the URL for submissions started via a shareable link.
    if (optionsId <= 0) {
        store.dispatch({
            type: NewDataCollectionParametersActions.Initialize,
            appData: defaultSettings,
        });

        return;
    }

    LogMessage(
        `Initializing portal for a shared link, entityId: ${entityId} requestId: ${submissionRequestId} optionsId: ${optionsId}`,
        SeverityLevel.Information,
        {
            entityId,
            submissionRequestId,
            optionsId,
        }
    );

    store.dispatch({
        type: NewDataCollectionParametersActions.BeginInitializeFromURL,
        url: location.href,
    });

    let retries = 5;
    let lastException = undefined;

    while (retries > 0) {
        try {
            const options = await FetchSubmissionRequest(entityId, submissionRequestId);

            if (!!lastException) {
                LogMessage(
                    `Failed effort succeeded on retry attempting to retrieve submission request options`,
                    SeverityLevel.Information,
                    {
                        entityId,
                        submissionRequestId,
                        optionsId
                    }
                );
            }

            let uiOptions: PortalCreateSubmissionRequestOptions | undefined = undefined;

            if (!!options) {
                uiOptions = PortalSubmissionRequestFromSubmissionRequest(options);
            }

            store.dispatch({
                type: NewDataCollectionParametersActions.EndInitializeFromURL,
                url: location.href,
                optionsId: optionsId,
                options: uiOptions,
            });

            return;
        } catch (exception) {
            console.error(`Exception caught attempting to retrieve submission request options entityId: ${entityId} submissionRequestId: ${submissionRequestId} optionsId: ${optionsId}`);
            console.error(exception);

            lastException = exception;
            retries--;

            LogException(
                `Exception retrieving request options from submissionRequestId, entityId: ${entityId} requestId: ${submissionRequestId} optionsId: ${optionsId}`,
                exception,
                {
                    entityId,
                    submissionRequestId,
                    optionsId,
                    retries,
                },
            );

            // Delay for 2 seconds
            await Sleep(2000);
        }
    }

    // If we get here, we've retried too many times and nothing was successful.   If
    // we had a successful fetch call, we already dispatched complete and returned.

    store.dispatch({
        type: NewDataCollectionParametersActions.EndInitializeFromURL,
        url: location.href,
        optionsId: -1,
        options: undefined,
        initializationException: lastException,
    });
}

function SetCurrentSubmissionRequest(
    dataCollectionSubmissionRequest: IDataCollectionSubmissionRequest
): AppThunkActionAsync<void, KnownAction> {
    return async (dispatch, getState) => {
        dispatch({
            type: NewDataCollectionParametersActions.BeginCurrentSubmissionRequest,
            dataCollectionSubmissionRequest: dataCollectionSubmissionRequest,
        });

        // No borrower is authorized to POST /SubmissionRequests: the anonymous PSS cookie 401s, and the
        // secure-URL SharedAccessToken cookie 403s — its grant (SubmitFinancialsRedirectArguments) deliberately
        // gives SubmissionRequests.Get (read the lender's existing request), NOT .Create. So skip it and advance
        // straight to requestCreated with no server request; CreateWorkspaceSubmission then creates the
        // submission against the EXISTING request (the shareable link's, or none for anonymous), mirroring V1.
        if (dataCollectionSubmissionRequest.sourceFlow === 'borrower') {
            dispatch({
                type: NewDataCollectionParametersActions.SetSubmissionRequestCreated,
                submissionRequest: null,
            });
            return;
        }

        try {
            const submissionRequest = await CreateSubmissionRequest(dataCollectionSubmissionRequest.workspaceId, dataCollectionSubmissionRequest.options);
            dispatch({
                type: NewDataCollectionParametersActions.SetSubmissionRequestCreated,
                submissionRequest: submissionRequest,
            });
        } catch {
            dispatch({
                type: NewDataCollectionParametersActions.SetSubmissionRequestError,
            });
        }
    };
}

function ClearCurrentSubmissionRequest(): AppThunkAction<void, KnownAction> {
    return (dispatch) => {
        dispatch({
            type: NewDataCollectionParametersActions.ClearCurrentSubmission,
        });
    };
}

function CompleteCurrentSubmission(): AppThunkAction<void, KnownAction> {
    return (dispatch) => {
        dispatch({
            type: NewDataCollectionParametersActions.CompleteCurrentSubmission,
        });
    };
}

function SetSelectedAccountingPackage(
    accountingPackage: AccountingPackage | string | null
): AppThunkActionAsync<void, KnownAction> {
    return async (dispatch, getState) => {
        const currentDataCollectionSubmissionRequest = GetCurrentDataCollectionSubmissionRequest(getState());
        if (!currentDataCollectionSubmissionRequest) {
            throw new Error("should not be possible");
        }
        switch (currentDataCollectionSubmissionRequest.linkMode) {
            case LinkMode.direct: {
                dispatch({
                    type: NewDataCollectionParametersActions.SetSelectedAccountingPackage,
                    accountingPackage: accountingPackage // TODO: fix type mapping
                });

                // No existing-connection lookup. The lender "connect directly" flow always creates a NEW
                // connection, and no borrower is authorized to list workspace connections (the anonymous PSS
                // cookie 401s; the secure-URL SharedAccessToken grant has no Connections.List). The connect
                // itself reuses-or-creates against the Strongbox API via the delegated token, exactly as V1
                // does — a not-logged-in borrower simply always creates a new connection. So resolve to
                // "no existing connection" → the confirmation modal shows just "Connect".
                if (!!accountingPackage) {
                    dispatch({
                        type: NewDataCollectionParametersActions.SetExistingConnection,
                        accountingPackage,
                        connection: null,
                    });
                }
                break;
            }
            case LinkMode.shareable: {
                // TODO: We may need to do something here on the borrower flow
                return;
            }
            case LinkMode.anonymous:
            default:
                throw new Error("Not implemented LinkMode.");
        }
    };

}

// Returns true only when the connection AND the import were successfully started (the flow is torn down via
// CompleteCurrentSubmission). Every non-success path — token failure, poll error, user-closed popup, import
// failure, or exception — closes the popup and dispatches 'aborted', which the flow view maps back to
// 'confirmConnect' so the confirmation modal reappears for a retry. Per product decision the connect flow
// never surfaces a dismissible error modal; failures are logged for diagnostics instead.
function StartWorkspaceSubmissionImport(connectWindow: ConnectWindow, isCancelled: () => boolean): AppThunkActionAsync<boolean, KnownAction> {
    return async (dispatch, getState) => {
        // connectWindow was opened synchronously on the user's click (the component side), so the
        // popup already exists. If anything below throws we must close it so we don't strand a
        // blank placeholder popup.
        try {
            const currentDataCollectionSubmissionRequest = GetCurrentDataCollectionSubmissionRequest(getState());
            const strongboxUri = GetStrongboxUrl(getState());
            const currentAccountingPackage = GetCurrentSubmissionAccountingSystem(getState());

            if (!currentAccountingPackage) {
                throw new Error("An Accounting Package must be selected before starting an import");
            }

            const submissionId = GetCurrentSubmissionId(getState());

            if (!submissionId) {
                throw new Error("A Submission must be created before starting an import");
            }

            // The flow this connection is for: 'borrower' for the secure-URL flow (set by BorrowerPortalV2),
            // 'lender' for a direct portal connection. It must ride the WHOLE connection (token descriptor,
            // connect-request descriptor, and import request) — the connector (e.g. Business Central) keys
            // prerequisite behaviour off 'borrower', so a mismatch makes it treat a borrower as a lender.
            const sourceFlow = currentDataCollectionSubmissionRequest?.sourceFlow ?? 'lender';

            // Enter the connecting state up front so the "Waiting on connection…" view covers the whole
            // connection-establishment operation (token retrieval, descriptor, navigate, and polling),
            // not just the popup wait.
            dispatch({
                type: NewDataCollectionParametersActions.SetAccountingSystemConnectionState,
                connectionState: AccountingSystemConnectionState.connecting,
            });

            const organizationsService = new OrganizationsService();
            const { descriptor, error: tokenError } = await RetrieveConnectionTokenWithErrorHandling(
                { nameId: currentAccountingPackage } as AccountingPackageDefinition,
                currentDataCollectionSubmissionRequest.workspaceId,
                currentDataCollectionSubmissionRequest.options,
                submissionId,
                strongboxUri,
                true,
                sourceFlow,
            );
            if (tokenError) {
                LogMessage(
                    `Connect token retrieval failed; reverting to the confirmation modal: ${tokenError.summaryMessage} ${tokenError.extraInformation ?? ''}`,
                    SeverityLevel.Warning
                );
                dispatch({
                    type: NewDataCollectionParametersActions.SetAccountingSystemConnectionState,
                    connectionState: AccountingSystemConnectionState.aborted,
                });
                connectWindow.close();
                return false;
            }
            dispatch({
                type: NewDataCollectionParametersActions.SetConnectionDescriptor,
                descriptor: descriptor,
            });
            // Uses the request's flow (computed above): the borrower connect endpoint is created with
            // mode='borrower' (see OrganizationsService) and behaves differently from the lender one.
            const connectionRequestDescriptor = await organizationsService.StartGetFinancialsConnectionDescriptor({
                accountingPackage: { nameId: currentAccountingPackage } as AccountingPackageDefinition,
                delegatedAccessToken: descriptor.delegatedAccessToken,
                sourceFlow,
                strongboxUri,
                orgId: currentDataCollectionSubmissionRequest.workspaceId
            });

            // The universal collector expects its collection parameters appended to the connect URL as
            // query params (cn, cur, fyem, uc, red, boa, np, …) — the same metadata we persisted on the
            // submission request. Build them from options.metadata so BOTH the popup navigation and the
            // copy-link fallback carry them; without this the connector opens with no company/period/etc.
            // (Non-universal packages have no metadata, so this is an empty list and a no-op for them.)
            const connectParameters: ConnectUrlParameter[] = (currentDataCollectionSubmissionRequest.options.metadata || [])
                .map(m => ({ key: m.id, value: m.value }));

            // Carry the workspace's own display name so downstream systems (Universal Collector, U2) can
            // report which DE workspace a collection belongs to without a separate lookup.
            if (currentDataCollectionSubmissionRequest.workspaceName) {
                connectParameters.push({ key: ImportMetadataKeys.ucWorkspaceDisplayName, value: currentDataCollectionSubmissionRequest.workspaceName });
            }

            // Surface the resolved connect URL (with the metadata params) so the "Waiting on connection…"
            // view can offer a manual fallback link (the universal collector modal's Copy Link button).
            // This is the exact URL the popup is navigated to below.
            if (connectionRequestDescriptor?.connectionEndpoint) {
                dispatch({
                    type: NewDataCollectionParametersActions.SetConnectEndpoint,
                    connectEndpoint: AppendMetadataToConnectionEndpoint(connectionRequestDescriptor.connectionEndpoint, connectParameters),
                });
            }

            // The poll loop runs until the connection finishes, the popup closes, OR isCancelled() (the
            // "Waiting on connection…" modal's Cancel button) goes true. Separately, flowAbandoned()
            // reports whether the flow has been torn down — Cancel clears currentSubmission, as can the
            // wizard reopening — so the callbacks and the post-connect handling below no-op in that case,
            // since the reducer requires a live currentSubmission and we must not resurrect a dead flow.
            const flowAbandoned = () => !GetCurrentDataCollectionSubmissionRequest(getState());

            // Exactly one of the callbacks below fires. outcome records which, so we only tear the flow down
            // on success. An abort (user closed the popup) reverts to the confirmation modal for a retry; a
            // genuine connector error surfaces the dedicated error modal with the message (see onError).
            let outcome: AccountingSystemConnectionState | undefined;

            // Set when the connector reports UserMustCompletePrerequisites (see onError). The connection
            // succeeded but the user still has prerequisite tasks to finish on their side, so the flow is
            // completed as a success (the submission marked pending) rather than surfacing the error modal.
            let prerequisitesRequired = false;

            const onError = (msg: string, detailedMsg: string, errorCode?: string) => {
                if (flowAbandoned()) {
                    return;
                }

                // This handles a specific case that's defined in the connector contract.  If the user
                // successfully connects but doesn't complete some tasks that require user intervention
                // on the portal side, it fails with the following code.  We should actually treat that
                // as success but mark the submission accordingly. The submission status update and flow
                // completion happen after ConnectAccountingSystem returns (below), where we can await them.

                if (!!errorCode && errorCode === 'UserMustCompletePrerequisites') {
                    LogMessage(
                        `Connect reported prerequisites required; marking submission pending user intervention: ${msg} ${detailedMsg}`,
                        SeverityLevel.Information,
                        { errorCode }
                    );
                    prerequisitesRequired = true;
                    return;
                }

                outcome = AccountingSystemConnectionState.error;
                LogMessage(
                    `Connect failed; surfacing the error modal: ${msg} ${detailedMsg}`,
                    SeverityLevel.Warning,
                    { errorCode }
                );
                // A genuine connector failure (as opposed to the user closing the popup) surfaces the
                // dedicated error modal (GetFinancialsImportFlowView maps 'error' -> the 'error' view) with
                // the message, rather than silently reverting to the confirmation modal like an abort.
                dispatch({
                    type: NewDataCollectionParametersActions.SetAccountingSystemConnectionState,
                    connectionState: AccountingSystemConnectionState.error,
                    connectionError: { msg, detailedMsg, errorCode },
                });
            };

            // ConnectAccountingSystem hands back the request with existingConnectionId populated with the
            // connection it just established. Capture that id: connecting and importing are two distinct
            // server calls, and ConnectAccountingSystem only performs the former — we start the import below.
            let connectedConnectionId: string | undefined;

            const onConnected = (connectedCxnRequest?: StrongboxConnectionRequest) => {
                if (flowAbandoned()) return;
                outcome = AccountingSystemConnectionState.connected;
                connectedConnectionId = connectedCxnRequest?.existingConnectionId;
                dispatch({
                    type: NewDataCollectionParametersActions.SetAccountingSystemConnectionState,
                    connectionState: AccountingSystemConnectionState.connected,
                });
            };

            const onAborted = () => {
                if (flowAbandoned()) return;
                outcome = AccountingSystemConnectionState.aborted;
                dispatch({
                    type: NewDataCollectionParametersActions.SetAccountingSystemConnectionState,
                    connectionState: AccountingSystemConnectionState.aborted,
                });
            };

            // Navigate the already-open popup to the real connect endpoint (with the metadata params). If
            // the user closed the placeholder popup while we were setting up, navigate() returns false and
            // we treat it as an abort.
            if (!connectWindow.navigate(connectionRequestDescriptor, connectParameters)) {
                onAborted();
            } else {
                await ConnectAccountingSystem(
                    descriptor,
                    connectionRequestDescriptor,
                    connectWindow.window,
                    onError,
                    onConnected,
                    onAborted,
                    isCancelled,
                    true
                );

                // The flow was torn down mid-connect (Cancel sets isCancelled, closes the popup, and
                // clears currentSubmission): nothing left to do but make sure the popup is closed and
                // stop. The callbacks no-op in this case, so outcome is still undefined.
                if (flowAbandoned()) {
                    connectWindow.close();
                    return false;
                }

                // The connector established the connection but signalled that the user still has prerequisite
                // tasks to finish on their side before data can flow (UserMustCompletePrerequisites). Treat
                // this as a successful connect (mirrors V1 BorrowerPortal's onPrerequisitesRequired): persist
                // the "pending user intervention" status and complete the flow. There is no import to start —
                // the connection can't yield data until the user finishes those prerequisites.
                if (prerequisitesRequired) {
                    connectWindow.close();
                    // UpdateSubmissionStatus self-handles its errors (logs + stores the failure in the reducer,
                    // never rethrows), and this whole block is inside the thunk's outer try/catch, so the await
                    // can't strand us before CompleteCurrentSubmission.
                    await dispatch(SubmissionActionCreators.UpdateSubmissionStatus(
                        currentDataCollectionSubmissionRequest.workspaceId,
                        submissionId,
                        'ConnectionPendingUserIntervention',
                        toAccountingPackageNameId(currentAccountingPackage),
                    ));
                    dispatch(CompleteCurrentSubmission());
                    return true;
                }

                if (outcome === undefined) {
                    throw new Error('ConnectAccountingSystem completed without invoking onError, onConnected, or onAborted');
                }
            }

            // The connect operation has resolved (success, abort, or failure), so close the popup before
            // we touch the flow state. Without this the OAuth window is left orphaned behind the UI —
            // e.g. on success CompleteCurrentSubmission tears the modals down but the popup stays open.
            // No-op if the user already closed it (the abort case).
            //
            // EXCEPTION — the Excel / file-upload connector: mirroring V1, we kick off the import (a no-op
            // server-side for this connector) but LEAVE the connect window OPEN so the user can finish the
            // upload there. Closing it here is exactly the bug where "the window just goes away" after the
            // first page. Still close it for a non-success outcome (abort/error), where there's nothing to
            // finish. `currentAccountingPackage` is guaranteed set (guarded at the top of this thunk).
            const isExcelUpload = currentAccountingPackage.toLowerCase() === ExcelUploadAccountingPackageNameId.toLowerCase();
            if (!(isExcelUpload && outcome === AccountingSystemConnectionState.connected)) {
                connectWindow.close();
            }

            if (outcome !== AccountingSystemConnectionState.connected) {
                // Aborted (user closed the popup) or a connector error: the callbacks already dispatched the
                // terminal state ('aborted' -> confirmation modal, 'error' -> error modal). Nothing more to do.
                return false;
            }

            // Connecting only established the connection; we still have to kick off the import itself
            // (FinancialRecords/Import) against the new connection, exactly as the reuse path does.
            // Without this the flow completes with no import started.
            if (!connectedConnectionId) {
                LogMessage(
                    'Connected but no connection id was returned; reverting to the confirmation modal',
                    SeverityLevel.Warning
                );
                dispatch({
                    type: NewDataCollectionParametersActions.SetAccountingSystemConnectionState,
                    connectionState: AccountingSystemConnectionState.aborted,
                });
                return false;
            }

            const connectionOptions: ConnectionOptions = {
                connectorType: currentDataCollectionSubmissionRequest.connectorType,
                workspaceId: currentDataCollectionSubmissionRequest.workspaceId,
                workspaceName: currentDataCollectionSubmissionRequest.workspaceName,
                linkMode: currentDataCollectionSubmissionRequest.linkMode,
                chooseAccountingSystem: false,
                options: currentDataCollectionSubmissionRequest.options,
            };

            const cxnRequest: StrongboxConnectionRequest = {
                accountingPackage: descriptor.accountingPackage,
                delegatedAccessToken: descriptor.delegatedAccessToken,
                strongboxUri: descriptor.strongboxUri,
                orgId: descriptor.orgId,
                existingConnectionId: connectedConnectionId,
                submissionId: descriptor.submissionId,
                sourceFlow,
            };

            // Exactly one of ExecuteImport's callbacks fires: import-started (success) tears the flow down;
            // failure logs and reverts to the confirmation modal (no error modal — product decision).
            let importSucceeded = false;

            await ExecuteImport(
                connectionOptions,
                cxnRequest,
                connectionRequestDescriptor,
                (summaryMsg, detailedMsg) => {
                    LogMessage(
                        `Import start failed; reverting to the confirmation modal: ${summaryMsg} ${detailedMsg}`,
                        SeverityLevel.Warning
                    );
                },
                () => {
                    importSucceeded = true;
                },
            );

            if (importSucceeded) {
                dispatch(CompleteCurrentSubmission());
                return true;
            }

            dispatch({
                type: NewDataCollectionParametersActions.SetAccountingSystemConnectionState,
                connectionState: AccountingSystemConnectionState.aborted,
            });
            return false;
        } catch (e) {
            connectWindow.close();
            LogException('Error starting workspace submission import; reverting to the confirmation modal', e);
            // Revert to the confirmation modal (no error modal — product decision). currentSubmission is
            // non-null here, so the reducer guard on SetAccountingSystemConnectionState will not throw.
            dispatch({
                type: NewDataCollectionParametersActions.SetAccountingSystemConnectionState,
                connectionState: AccountingSystemConnectionState.aborted,
            });
            return false;
        }
    }
}

function CreateWorkspaceSubmission(): AppThunkActionAsync<void, KnownAction> {
    return async (dispatch, getState) => {
        const currentDataCollectionSubmissionRequest = GetCurrentDataCollectionSubmissionRequest(getState());
        const currentSubmissionRequest = GetCurrentSubmissionRequest(getState());
        const isBorrower = currentDataCollectionSubmissionRequest?.sourceFlow === 'borrower';
        const isAnonymous = !!currentDataCollectionSubmissionRequest?.isAnonymous;
        // The universal collector ("upload files yourself") is a one-way file upload whose submission is
        // created SERVER-SIDE when the uploaded file's financial record is created
        // (FinancialSubmissionEventHandler.IsPortalSubmission). We must NOT create one in the portal — this
        // mirrors V1's skipSubmissionCreation and is the only flow it applies to, for both the borrower and
        // the lender.
        const skipSubmissionCreation =
            GetCurrentSubmissionAccountingSystem(getState())?.toLowerCase() === UniversalCollectorAccountingPackageNameId;
        // Borrower flows intentionally have no server submission request they created (see
        // SetCurrentSubmissionRequest); the lender request-based flow must have one — unless we are skipping
        // submission creation (universal collector), where there is no request either.
        if (!currentDataCollectionSubmissionRequest || (!isBorrower && !skipSubmissionCreation && !currentSubmissionRequest)) {
            throw new Error('Cannot create a workspace submission before a submission request has been created');
        }
        try {
            // State-only transition: preserves accountingPackage and everything else set earlier.
            dispatch({
                type: NewDataCollectionParametersActions.SetCreatingSubmission,
            });

            // Universal collector: create no submission here. Use the 'undefined' placeholder (matching V1)
            // so the downstream connect guards — which require a truthy submission id — pass, while sending
            // no real SubmissionId. The server then CREATES the submission on upload rather than associating
            // to an existing one. Returns before the Borrower-store publish below (there is no real id).
            if (skipSubmissionCreation) {
                dispatch({
                    type: NewDataCollectionParametersActions.SetCurrentSubmission,
                    submissionId: 'undefined',
                });
                return;
            }

            // The borrower creates a submission against the EXISTING request: the shareable link's request
            // (requestedOptionsId; the borrower holds SubmissionRequests.Get for it) or none for the anonymous
            // flow, which has no link. The lender uses the request it just created. The anonymous flow also
            // tags the submission with the business name/email the user entered (so the lender can identify the
            // borrower) and sets the verification email. Mirrors V1, which passed shareableLinkOptionsId
            // (== requestedOptionsId) to CreateSubmission and never created a request from the borrower.
            let submissionMeta: Map<string, string> | undefined = undefined;
            let verificationEmail: string | undefined = undefined;
            if (isAnonymous) {
                const businessInfo = GetAcceptedBusinessInfo(getState());
                if (!!businessInfo.name && !!businessInfo.email) {
                    submissionMeta = new Map<string, string>();
                    submissionMeta.set(SubmissionMetaBorrowerBusinessName, businessInfo.name);
                    submissionMeta.set(SubmissionMetaBorrowerBusinessEmail, businessInfo.email);
                    verificationEmail = businessInfo.email;
                }
            }

            const requestId = isBorrower
                ? GetRequestedOptionsId(getState())
                : currentSubmissionRequest?.id;

            const submissionId: string = await CreateSubmission(
                currentDataCollectionSubmissionRequest.workspaceId,
                requestId,
                submissionMeta,
                verificationEmail
            );
            dispatch({
                type: NewDataCollectionParametersActions.SetCurrentSubmission,
                submissionId: submissionId
            });

            // The borrower's end-of-flow actions (email a copy, upload additional documents) read the
            // submission id from the Borrower store, not this one. The universal-upload borrower flow skips
            // submission creation (returned above), so this only runs for the accounting-package borrower
            // flows that stay in the portal to reach those actions — publish the id so they have one to use.
            if (isBorrower) {
                store.dispatch(BorrowerActionCreators.SetBorrowerSubmissionId(submissionId));
            }
        } catch {
            dispatch({
                type: NewDataCollectionParametersActions.SetSubmissionError,
            });
        }
    }
}

// Returns true only when the connection AND import were started (see StartWorkspaceSubmissionImport). If the
// submission could not be created, StartWorkspaceSubmissionImport's submissionId guard throws and its catch
// reverts to the confirmation modal, returning false.
function CreateWorkspaceSubmissionAndStartImport(connectWindow: ConnectWindow, isCancelled: () => boolean): AppThunkActionAsync<boolean, KnownAction> {
    return async (dispatch) => {
        await dispatch(CreateWorkspaceSubmission());
        return await dispatch(StartWorkspaceSubmissionImport(connectWindow, isCancelled));
    }
}

// The universal-collector connect step. Unlike the OAuth packages (StartWorkspaceSubmissionImport), the
// universal collector is a ONE-WAY FILE UPLOAD the user completes in the popup. The submission and the
// connect request are set up server-side, so we navigate the popup to the upload page and STOP. We do NOT
// poll for a connection or start a client-side import: the universal collector connection reports "Success"
// as soon as the request is created (there is no OAuth round-trip), so polling would fire onConnected
// immediately and tear the modal/popup down before the user could upload anything. Instead the flow stays
// in 'connecting' ('waitingOnConnection'), which renders the "complete in the new window / copy link" modal
// until the user closes it (Close finalizes via CompleteCurrentSubmission).
// Drives the universal-collector connect for both the lender ("upload files yourself", popup) and the
// borrower (secure link, full-page redirect) flows. The navigator abstracts how the user reaches the
// connect endpoint (ConnectWindow.navigate vs RedirectConnectNavigator.navigate); sourceFlow tags the
// connection descriptor and selects the borrower-specific UI behaviour below.
function StartUniversalConnectorConnect(
    navigator: IConnectNavigator,
    sourceFlow: 'lender' | 'borrower' = 'lender'
): AppThunkActionAsync<void, KnownAction> {
    return async (dispatch, getState) => {
        try {
            const currentDataCollectionSubmissionRequest = GetCurrentDataCollectionSubmissionRequest(getState());
            const strongboxUri = GetStrongboxUrl(getState());
            const currentAccountingPackage = GetCurrentSubmissionAccountingSystem(getState());

            if (!currentDataCollectionSubmissionRequest || !currentAccountingPackage) {
                throw new Error('An accounting package and submission request are required before connecting');
            }

            const submissionId = GetCurrentSubmissionId(getState());
            if (!submissionId) {
                throw new Error('A submission must be created before connecting');
            }

            // Lender (popup): enter 'connecting' so GetFinancialsImportFlowView returns 'waitingOnConnection'
            // and renders the "complete in the new window / copy link" modal while the popup is open. The
            // borrower redirect replaces the whole page, so we deliberately stay on the generic 'working'
            // spinner (no Cancel, no popup modal) right up to the redirect — the user chose nothing and
            // confirms nothing, matching "go straight through to the upload connector".
            if (sourceFlow === 'lender') {
                dispatch({
                    type: NewDataCollectionParametersActions.SetAccountingSystemConnectionState,
                    connectionState: AccountingSystemConnectionState.connecting,
                });
            }

            const { descriptor, error: tokenError } = await RetrieveConnectionTokenWithErrorHandling(
                { nameId: currentAccountingPackage } as AccountingPackageDefinition,
                currentDataCollectionSubmissionRequest.workspaceId,
                currentDataCollectionSubmissionRequest.options,
                submissionId,
                strongboxUri,
                true,
                sourceFlow,
            );
            if (tokenError) {
                dispatch({
                    type: NewDataCollectionParametersActions.SetAccountingSystemConnectionState,
                    connectionState: AccountingSystemConnectionState.error,
                    connectionError: {
                        msg: tokenError.summaryMessage,
                        detailedMsg: tokenError.extraInformation ?? '',
                    },
                });
                navigator.close();
                return;
            }

            dispatch({
                type: NewDataCollectionParametersActions.SetConnectionDescriptor,
                descriptor,
            });

            const organizationsService = new OrganizationsService();
            const connectionRequestDescriptor = await organizationsService.StartGetFinancialsConnectionDescriptor({
                accountingPackage: { nameId: currentAccountingPackage } as AccountingPackageDefinition,
                delegatedAccessToken: descriptor.delegatedAccessToken,
                sourceFlow,
                strongboxUri,
                orgId: currentDataCollectionSubmissionRequest.workspaceId,
            });

            // The universal collector expects its collection parameters appended to the connect URL (cn,
            // cur, fyem, uc, red, boa, np, …) — the metadata we persisted on the submission request. Build
            // them so both the popup navigation and the copy-link fallback carry them.
            const connectParameters: ConnectUrlParameter[] = (currentDataCollectionSubmissionRequest.options.metadata || [])
                .map(m => ({ key: m.id, value: m.value }));

            // Carry the workspace's own display name so downstream systems (Universal Collector, U2) can
            // report which DE workspace a collection belongs to without a separate lookup.
            if (currentDataCollectionSubmissionRequest.workspaceName) {
                connectParameters.push({ key: ImportMetadataKeys.ucWorkspaceDisplayName, value: currentDataCollectionSubmissionRequest.workspaceName });
            }

            // Also carry the submission request id (subreqid) so the server associates the uploaded record with
            // the request created up front rather than synthesizing a new one from the ConsumerMetadata. The
            // lender's id comes from the request just created (CreateUniversalConnectorSubmissionAndStartImport);
            // the borrower reuses the secure link's existing request id (it can't create one).
            const submissionRequestId = sourceFlow === 'borrower'
                ? GetRequestedOptionsId(getState())
                : GetCurrentSubmissionRequest(getState())?.id;
            if (submissionRequestId !== undefined && submissionRequestId !== null) {
                connectParameters.push({ key: ImportMetadataKeys.ucSubmissionRequestId, value: submissionRequestId.toString() });
            }

            if (connectionRequestDescriptor?.connectionEndpoint) {
                dispatch({
                    type: NewDataCollectionParametersActions.SetConnectEndpoint,
                    connectEndpoint: AppendMetadataToConnectionEndpoint(connectionRequestDescriptor.connectionEndpoint, connectParameters),
                });
            }

            // Send the user to the upload page and stop (no poll / import — see the comment above). For the
            // lender this navigates the popup; for the borrower it replaces the page (RedirectConnectNavigator).
            navigator.navigate(connectionRequestDescriptor, connectParameters);
        } catch (e) {
            navigator.close();
            LogException('Error starting universal collector connect', e);
            dispatch({
                type: NewDataCollectionParametersActions.SetAccountingSystemConnectionState,
                connectionState: AccountingSystemConnectionState.error,
                connectionError: {
                    msg: 'Connection failed',
                    detailedMsg: 'We were unable to start the universal collector connection. Please try again.',
                },
            });
        }
    };
}

// The universal-collector ("upload files yourself") counterpart to the picker-driven flow. The universal
// collector is not a package the user chooses from the grid and it never reuses an existing connection, so
// this mirrors V1's immediateConnectPackage + SkipImportConfirmation: create the request, force-select the
// universal collector package WITHOUT the existing-connection lookup, create the submission, then hand off
// to StartUniversalConnectorConnect (navigate the popup, no poll/import). The existing-connection lookup
// stays 'pending' and GetFinancialsImportFlowView special-cases universalConnector, so the flow never
// flashes the 'selectAccountingSystem' picker or the 'confirmConnect' modal.
//
// For the lender ("upload files yourself"), the navigator is a ConnectWindow opened synchronously by the
// caller inside the user's click (transient activation for window.open), navigated to the real connect URL
// once the async setup below completes. For the borrower (secure UC link, sourceFlow 'borrower') it is a
// RedirectConnectNavigator that replaces the page — there is no popup to pre-open.
function CreateUniversalConnectorSubmissionAndStartImport(
    dataCollectionSubmissionRequest: IDataCollectionSubmissionRequest,
    navigator: IConnectNavigator,
    sourceFlow: 'lender' | 'borrower' = 'lender'
): AppThunkActionAsync<void, KnownAction> {
    return async (dispatch, getState) => {
        // Tag the request with the flow so CreateWorkspaceSubmission (below) reuses the link's existing request
        // for the borrower vs the one created here for the lender. (The borrower UC path builds the request
        // without a sourceFlow and passes 'borrower' only as the thunk arg.)
        const request: IDataCollectionSubmissionRequest = { ...dataCollectionSubmissionRequest, sourceFlow };

        dispatch({
            type: NewDataCollectionParametersActions.BeginCurrentSubmissionRequest,
            dataCollectionSubmissionRequest: request,
        });

        // The universal collector never creates a *submission* in the portal — that happens server-side when
        // the uploaded file is processed (mirrors V1's skipSubmissionCreation; CreateWorkspaceSubmission below
        // skips it and sets the connect placeholder). It DOES, however, create a *submission request* up front
        // so the server can associate the uploaded record with it (via the 'subreqid' URL metadata appended in
        // StartUniversalConnectorConnect) instead of synthesizing a fresh one from ConsumerMetadata.
        //
        // Only the lender creates it here: the borrower isn't authorized to POST /SubmissionRequests (its
        // SharedAccessToken grant is SubmissionRequests.Get, not .Create) and doesn't need to — the request was
        // already created by the lender when the secure URL was made, and the borrower reuses its id
        // (GetRequestedOptionsId) as 'subreqid'.
        if (sourceFlow === 'borrower') {
            dispatch({
                type: NewDataCollectionParametersActions.SetSubmissionRequestCreated,
                submissionRequest: null,
            });
        } else {
            try {
                const submissionRequest = await CreateSubmissionRequest(request.workspaceId, request.options);
                dispatch({
                    type: NewDataCollectionParametersActions.SetSubmissionRequestCreated,
                    submissionRequest,
                });
            } catch {
                dispatch({
                    type: NewDataCollectionParametersActions.SetSubmissionRequestError,
                });
                navigator.close();
                return;
            }
        }

        // Force-select the universal collector. Dispatch the raw action (not SetSelectedAccountingPackage,
        // which also kicks off the existing-connection lookup) because the universal collector never reuses
        // a connection.
        dispatch({
            type: NewDataCollectionParametersActions.SetSelectedAccountingPackage,
            accountingPackage: UniversalCollectorAccountingPackageNameId,
        });

        await dispatch(CreateWorkspaceSubmission());

        // CreateWorkspaceSubmission transitions to submissionError (and the flow view shows the error modal)
        // if it fails; only proceed to the connect when a submission was actually created.
        if (!GetCurrentSubmissionId(getState())) {
            navigator.close();
            return;
        }

        await dispatch(StartUniversalConnectorConnect(navigator, sourceFlow));
    }
}

// The reuse counterpart to StartWorkspaceSubmissionImport: the workspace already has a connection for the
// selected package, so there is no OAuth popup. We mint a delegated token bound to that existing connection
// (RetrieveConnectionToken with ignoreConnections=false) and start the import directly via ExecuteImport.
// Both paths start the import client-side via ExecuteImport; this one differs only in that it skips the
// connect-descriptor/popup flow and reuses the existing connection id instead of establishing a new one.
// There is no popup: submissionCreated + idle renders the generic "working" spinner until the import starts
// (success) or fails (error modal).
function StartWorkspaceSubmissionImportWithExistingConnection(existingConnectionId?: string): AppThunkActionAsync<void, KnownAction> {
    return async (dispatch, getState) => {
        try {
            const currentDataCollectionSubmissionRequest = GetCurrentDataCollectionSubmissionRequest(getState());
            const strongboxUri = GetStrongboxUrl(getState());
            const currentAccountingPackage = GetCurrentSubmissionAccountingSystem(getState());

            if (!currentAccountingPackage) {
                throw new Error("An Accounting Package must be selected before starting an import");
            }

            const submissionId = GetCurrentSubmissionId(getState());

            if (!submissionId) {
                throw new Error("A Submission must be created before starting an import");
            }

            // 'borrower' for the secure-URL flow (a borrower can reuse an existing connection), 'lender' for a
            // direct portal connection. Carried through the token descriptor and the import request below.
            const sourceFlow = currentDataCollectionSubmissionRequest?.sourceFlow ?? 'lender';

            const { descriptor, error: tokenError } = await RetrieveConnectionTokenWithErrorHandling(
                { nameId: currentAccountingPackage } as AccountingPackageDefinition,
                currentDataCollectionSubmissionRequest.workspaceId,
                currentDataCollectionSubmissionRequest.options,
                submissionId,
                strongboxUri,
                // A specific connection id (the "Sync financial data" row) means "use exactly this one" — don't
                // auto-find. Without one, let the token retrieval reuse the workspace's connection for the package.
                !!existingConnectionId,
                sourceFlow,
            );
            if (tokenError) {
                dispatch({
                    type: NewDataCollectionParametersActions.SetAccountingSystemConnectionState,
                    connectionState: AccountingSystemConnectionState.error,
                    connectionError: {
                        msg: tokenError.summaryMessage,
                        detailedMsg: tokenError.extraInformation ?? '',
                    },
                });
                return;
            }

            // Prefer the caller-supplied connection id (the synced row); otherwise use the one the token
            // retrieval resolved for this package.
            const reuseConnectionId = existingConnectionId ?? descriptor?.existingConnectionId;

            if (!descriptor || !reuseConnectionId) {
                // The connection we detected is no longer usable. Surface a dismissible error rather than
                // silently stalling on the spinner.
                dispatch({
                    type: NewDataCollectionParametersActions.SetAccountingSystemConnectionState,
                    connectionState: AccountingSystemConnectionState.error,
                    connectionError: {
                        msg: 'Connection unavailable',
                        detailedMsg: 'We could not use the existing connection for this company. Please reconnect and try again.',
                    },
                });
                return;
            }

            const reuseDescriptor = { ...descriptor, existingConnectionId: reuseConnectionId };

            dispatch({
                type: NewDataCollectionParametersActions.SetConnectionDescriptor,
                descriptor: reuseDescriptor,
            });

            const cxnRequest: StrongboxConnectionRequest = {
                accountingPackage: reuseDescriptor.accountingPackage,
                delegatedAccessToken: reuseDescriptor.delegatedAccessToken,
                strongboxUri: reuseDescriptor.strongboxUri,
                orgId: reuseDescriptor.orgId,
                existingConnectionId: reuseConnectionId,
                submissionId: reuseDescriptor.submissionId,
                sourceFlow,
            };

            const connectionOptions: ConnectionOptions = {
                connectorType: currentDataCollectionSubmissionRequest.connectorType,
                workspaceId: currentDataCollectionSubmissionRequest.workspaceId,
                workspaceName: currentDataCollectionSubmissionRequest.workspaceName,
                linkMode: currentDataCollectionSubmissionRequest.linkMode,
                chooseAccountingSystem: false,
                options: currentDataCollectionSubmissionRequest.options,
            };

            // Exactly one of ExecuteImport's callbacks fires: import-started (success) tears the flow down;
            // error surfaces the dismissible error modal.
            let outcome: AccountingSystemConnectionState | undefined;

            await ExecuteImport(
                connectionOptions,
                cxnRequest,
                undefined,
                (summaryMsg, detailedMsg) => {
                    outcome = AccountingSystemConnectionState.error;
                    dispatch({
                        type: NewDataCollectionParametersActions.SetAccountingSystemConnectionState,
                        connectionState: AccountingSystemConnectionState.error,
                        connectionError: { msg: summaryMsg, detailedMsg },
                    });
                },
                () => {
                    outcome = AccountingSystemConnectionState.connected;
                },
            );

            if (outcome === AccountingSystemConnectionState.connected) {
                dispatch(CompleteCurrentSubmission());
            } else if (outcome === undefined) {
                dispatch({
                    type: NewDataCollectionParametersActions.SetAccountingSystemConnectionState,
                    connectionState: AccountingSystemConnectionState.error,
                    connectionError: {
                        msg: 'Import failed',
                        detailedMsg: 'We were unable to start the import from your existing connection. Please try again.',
                    },
                });
            }
        } catch (e) {
            LogException('Error starting workspace submission import with existing connection', e);
            dispatch({
                type: NewDataCollectionParametersActions.SetAccountingSystemConnectionState,
                connectionState: AccountingSystemConnectionState.error,
                connectionError: {
                    msg: 'Import failed',
                    detailedMsg: 'We were unable to import from your existing connection. Please try again.',
                },
            });
        }
    };
}

function CreateWorkspaceSubmissionAndReuseConnection(existingConnectionId?: string): AppThunkActionAsync<void, KnownAction> {
    return async (dispatch, getState) => {
        const existingSubmissionId = GetCurrentSubmissionId(getState());
        if (existingSubmissionId) {
            // A submission was already created in a prior (aborted) attempt.
            // We always recreate the submission
            // Reset the stale aborted connection state so GetFinancialsImportFlowView returns
            // 'working' instead of 'confirmConnect' during the async import below.
            dispatch({
                type: NewDataCollectionParametersActions.SetAccountingSystemConnectionState,
                connectionState: AccountingSystemConnectionState.idle,
                connectionError: undefined,
            });
        }
        await dispatch(CreateWorkspaceSubmission());
        await dispatch(StartWorkspaceSubmissionImportWithExistingConnection(existingConnectionId));
    }
}

// "Sync financial data from <accounting system>" (workspace → Manage Active Connections): the connection is
// already known, so this skips the package picker and the connect popup entirely. It creates the submission
// request/submission for the chosen collection options, force-selects the row's accounting package (raw, no
// existing-connection lookup), and imports directly against the supplied connection id.
function CreateSubmissionAndReuseExistingConnection(
    dataCollectionSubmissionRequest: IDataCollectionSubmissionRequest,
    accountingPackageNameId: string,
    existingConnectionId: string,
): AppThunkActionAsync<void, KnownAction> {
    return async (dispatch, getState) => {
        dispatch({
            type: NewDataCollectionParametersActions.BeginCurrentSubmissionRequest,
            dataCollectionSubmissionRequest,
        });

        try {
            const submissionRequest = await CreateSubmissionRequest(dataCollectionSubmissionRequest.workspaceId, dataCollectionSubmissionRequest.options);
            dispatch({
                type: NewDataCollectionParametersActions.SetSubmissionRequestCreated,
                submissionRequest,
            });
        } catch {
            dispatch({
                type: NewDataCollectionParametersActions.SetSubmissionRequestError,
            });
            return;
        }

        // Force-select the row's package with the raw action (not the SetSelectedAccountingPackage thunk,
        // which would kick off the existing-connection lookup) — we already know the connection to use.
        dispatch({
            type: NewDataCollectionParametersActions.SetSelectedAccountingPackage,
            accountingPackage: accountingPackageNameId,
        });

        await dispatch(CreateWorkspaceSubmissionAndReuseConnection(existingConnectionId));
    }
}

// Reports a hard popup-block to the store so the UI can surface it. Used when window.open returns
// null on the user's click (e.g. the browser has popups hard-blocked), before any thunk runs.
function ReportPopupBlocked(msg: string, detailedMsg: string): AppThunkAction<void, KnownAction> {
    return (dispatch) => {
        dispatch({
            type: NewDataCollectionParametersActions.SetAccountingSystemConnectionState,
            connectionState: AccountingSystemConnectionState.error,
            connectionError: { msg, detailedMsg, errorCode: 'POPUP_BLOCKED' },
        });
    };
}

export const actionCreators = {
    SetNewDataCollectionOptions,
    InitializeFromURL,
    InitializeCollectionGroupings,
    SetCurrentSubmissionRequest,
    ClearCurrentSubmissionRequest,
    SetSelectedAccountingPackage,
    CreateWorkspaceSubmissionAndStartImport,
    CreateUniversalConnectorSubmissionAndStartImport,
    CreateWorkspaceSubmissionAndReuseConnection,
    CreateSubmissionAndReuseExistingConnection,
    CompleteCurrentSubmission,
    ReportPopupBlocked,
};

